import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import Voltar from "@/components/Voltar";
import { brl, brlShort, num, plural } from "@/lib/format";
import { toCents, formatBRL } from "@/lib/money";
import { Layers, Loader2, Save, Search, Undo2, X } from "@/components/icons";

const INPUT = "w-full h-10 px-3 rounded-md border border-input bg-white text-sm outline-none focus:ring-2 focus:ring-brand/40";
const CELL_INPUT = "w-full h-9 px-2.5 rounded-md border border-transparent bg-transparent text-sm text-right focus:bg-white focus:border-brand/60 focus:ring-2 focus:ring-brand/30 outline-none";
const achTone = (a) => (a >= 100 ? "text-ok" : a >= 70 ? "text-warn" : "text-bad");

const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

// Janela fixa de meses editáveis — ver CORRECAO_EMERGENT_COTAS_MESES_FUTUROS.md.
// Ago/26 → Dez/27 (17 meses). Rodada em UTC igual ao backend.
function monthsList() {
  const now = new Date();
  const out = [];
  const win = [];
  for (let m = 8; m <= 12; m += 1) win.push({ y: 2026, m });
  for (let m = 1; m <= 12; m += 1) win.push({ y: 2027, m });
  for (const { y, m } of win) {
    out.push({
      year: y, month: m,
      label: `${MESES[m - 1]} ${y}`,
      is_current: y === now.getFullYear() && m === now.getMonth() + 1,
    });
  }
  return out;
}

export default function CotasLote() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;
  // LOTE PERM-1.2: diretor também lança cotas em lote (dentro do escopo).
  // PAINEL-1.7 (item 7): diretor geral também pode lançar/editar cotas.
  const canSeeVendedores = role === "admin" || role === "manager" || role === "director" || role === "diretor_geral";
  const canSeeMinha = role === "admin" || role === "manager" || role === "director" || role === "diretor_geral";
  const canSeeMPires = role === "admin" || role === "diretor_geral";

  // ---------- Aba ativa ----------
  const initialAba = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const want = params.get("aba");
    if (want === "mpires" && canSeeMPires) return "mpires";
    if (want === "real" && canSeeMinha) return "real";
    return canSeeVendedores ? "vendedores" : (canSeeMinha ? "real" : (canSeeMPires ? "mpires" : "vendedores"));
  }, [location.search, canSeeVendedores, canSeeMinha, canSeeMPires]);
  const [aba, setAba] = useState(initialAba);

  // Dirty-state (vendedores/real/mpires) para o aviso ao trocar.
  const [dirtyVend, setDirtyVend] = useState(false);
  const [dirtyMinha, setDirtyMinha] = useState(false);
  const [dirtyMPires, setDirtyMPires] = useState(false);

  const trocarAba = (destino) => {
    if (destino === aba) return;
    const abaAtualDirty = aba === "vendedores" ? dirtyVend : aba === "mpires" ? dirtyMPires : dirtyMinha;
    if (abaAtualDirty) {
      // PAINEL-1.7 (item 8) — bom no iPhone/Android, sem confirm nativo.
      const ok = window.confirm("Você tem alterações não salvas. Deseja descartar?");
      if (!ok) return;
    }
    setAba(destino);
    const params = new URLSearchParams(location.search);
    if (destino === "real") params.set("aba", "real");
    else if (destino === "mpires") params.set("aba", "mpires");
    else params.delete("aba");
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
  };

  if (!canSeeVendedores && !canSeeMinha && !canSeeMPires) {
    return <ErrorState message="Sem permissão para lançar cotas" />;
  }

  return (
    <div>
      <Voltar fallback="/equipe" className="mb-3" />
      <PageHeader title="Lançar cotas" subtitle="Cotas dos vendedores e sua cota real do ano." />

      {/* Abas */}
      <div className="mb-4 grid grid-cols-2 gap-2 sm:inline-flex sm:gap-0 sm:border sm:border-black/10 sm:rounded-xl sm:p-1 sm:bg-slate-50 w-full sm:w-auto">
        {canSeeVendedores && (
          <button
            type="button"
            onClick={() => trocarAba("vendedores")}
            data-testid="cotas-aba-vendedores"
            className={`h-10 px-4 text-sm font-semibold rounded-xl sm:rounded-lg transition-colors ${aba === "vendedores" ? "bg-brand text-white shadow-sm" : "bg-white sm:bg-transparent text-navy hover:bg-slate-100 border border-black/10 sm:border-0"}`}
          >
            Cotas dos vendedores
          </button>
        )}
        {canSeeMinha && (
          <button
            type="button"
            onClick={() => trocarAba("real")}
            data-testid="cotas-aba-real"
            className={`h-10 px-4 text-sm font-semibold rounded-xl sm:rounded-lg transition-colors ${aba === "real" ? "bg-brand text-white shadow-sm" : "bg-white sm:bg-transparent text-navy hover:bg-slate-100 border border-black/10 sm:border-0"}`}
          >
            {role === "diretor_geral" || role === "admin" ? "Cota real dos gestores" : "Minha cota real"}
          </button>
        )}
        {canSeeMPires && (
          <button
            type="button"
            onClick={() => trocarAba("mpires")}
            data-testid="cotas-aba-mpires"
            className={`h-10 px-4 text-sm font-semibold rounded-xl sm:rounded-lg transition-colors ${aba === "mpires" ? "bg-brand text-white shadow-sm" : "bg-white sm:bg-transparent text-navy hover:bg-slate-100 border border-black/10 sm:border-0"}`}
          >
            Cota da MPires
          </button>
        )}
      </div>

      {aba === "vendedores" && canSeeVendedores && (
        <AbaVendedores onDirtyChange={setDirtyVend} />
      )}
      {aba === "real" && canSeeMinha && (
        <AbaMinhaCotaReal onDirtyChange={setDirtyMinha} switchToVendedores={() => trocarAba("vendedores")} />
      )}
      {aba === "mpires" && canSeeMPires && (
        <AbaCotaMPires onDirtyChange={setDirtyMPires} />
      )}
    </div>
  );
}

// ======================================================================
// Aba 1: Cotas dos vendedores (comportamento original preservado)
// ======================================================================
function AbaVendedores({ onDirtyChange }) {
  const months = useMemo(monthsList, []);
  const initialKey = useMemo(() => {
    const cur = months.find((mm) => mm.is_current);
    return cur ? `${cur.year}-${cur.month}` : `${months[0].year}-${months[0].month}`;
  }, [months]);
  const [selectedKey, setSelectedKey] = useState(initialKey);
  const selected = useMemo(() => months.find((mm) => `${mm.year}-${mm.month}` === selectedKey) || months[0], [months, selectedKey]);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});    // uid → { goalCents, invCents }
  const [orig, setOrig] = useState({});    // uid → { goalCents, invCents }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/performance/team-month?year=${selected.year}&month=${selected.month}`);
      setData(r.data);
      const map = {};
      for (const row of r.data.rows || []) {
        map[row.user_id] = {
          goalCents: Math.round((row.goal || 0) * 100),
          invCents: Math.round((row.invoiced_sales || 0) * 100),
        };
      }
      setRows(map);
      setOrig(JSON.parse(JSON.stringify(map)));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  }, [selected.year, selected.month]);

  useEffect(() => { load(); }, [load]);

  const setField = (uid, field, cents) => setRows((prev) => ({ ...prev, [uid]: { ...prev[uid], [field]: cents } }));
  const resetRow = (uid) => setRows((prev) => ({ ...prev, [uid]: { ...orig[uid] } }));

  const dirtyIds = useMemo(() => Object.keys(rows).filter((uid) => {
    const a = rows[uid], b = orig[uid];
    return !b || a.goalCents !== b.goalCents;
  }), [rows, orig]);

  useEffect(() => { onDirtyChange(dirtyIds.length > 0); }, [dirtyIds.length, onDirtyChange]);

  const listaFiltrada = useMemo(() => {
    if (!data) return [];
    const query = q.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (roleFilter !== "all" && r.role !== roleFilter) return false;
      if (!query) return true;
      return (r.name || "").toLowerCase().includes(query)
        || (r.email || "").toLowerCase().includes(query)
        || (r.gestor || "").toLowerCase().includes(query);
    });
  }, [data, q, roleFilter]);

  const totalMeta = useMemo(() => Object.values(rows).reduce((s, r) => s + (r.goalCents || 0), 0) / 100, [rows]);
  const totalFat = useMemo(() => Object.values(rows).reduce((s, r) => s + (r.invCents || 0), 0) / 100, [rows]);
  const totalAch = totalMeta > 0 ? Math.round((totalFat / totalMeta) * 100) : 0;
  // Oculta a coluna "Gestor" quando todos os vendedores exibidos têm o
  // mesmo gestor (visão do Gerente) — reduz pressão de largura e evita a
  // rolagem horizontal entre 950 e 1030 px.
  const hideGestorCol = useMemo(() => {
    if (!listaFiltrada.length) return true;
    const first = listaFiltrada[0]?.gestor || "";
    return listaFiltrada.every((r) => (r.gestor || "") === first);
  }, [listaFiltrada]);

  const save = async () => {
    if (!dirtyIds.length) { toast.info("Nenhuma alteração para salvar"); return; }
    setSaving(true);
    try {
      const payload = {
        year: selected.year, month: selected.month,
        rows: dirtyIds.map((uid) => ({
          user_id: uid,
          goal: (rows[uid].goalCents || 0) / 100,
        })),
      };
      const r = await api.post(`/performance/users/bulk-month`, payload);
      const { updated, erros } = r.data || {};
      if (erros?.length) {
        toast.warning(`${updated === 1 ? "1 salvo" : `${updated} salvos`}, ${erros.length === 1 ? "1 com erro" : `${erros.length} com erro`}`);
      } else {
        toast.success(`${updated === 1 ? "1 cota atualizada" : `${updated} cotas atualizadas`} em ${data.label}`);
      }
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Loading />;
  if (error) return <ErrorState onRetry={load} message={error} />;

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-base font-semibold text-navy" data-testid="aba-vendedores-titulo">
            Cotas dos vendedores · {data?.label || ""}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data ? `${plural(data.rows?.length || 0, "vendedor", "vendedores")} no seu escopo` : "Carregando…"}
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving || !dirtyIds.length}
          data-testid="salvar-lote-btn"
          className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar alterações{dirtyIds.length ? ` (${dirtyIds.length})` : ""}
        </button>
      </div>

      {/* Controles topo */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Mês / Ano</label>
          <select
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            className={INPUT + " mt-1"}
            data-testid="lote-mes-select"
          >
            {months.map((mm) => (
              <option key={`${mm.year}-${mm.month}`} value={`${mm.year}-${mm.month}`}>
                {mm.label}{mm.is_current ? " (mês atual)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Buscar</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nome, e-mail ou gestor…"
              className={INPUT + " pl-9"}
              data-testid="lote-busca"
            />
            {q ? (
              <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy" aria-label="Limpar busca">
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Papel</label>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className={INPUT + " mt-1"}
            data-testid="lote-role-filter"
          >
            <option value="all">Todos</option>
            <option value="seller">Vendedores</option>
            <option value="director">Diretores (Contas Diretas)</option>
          </select>
        </div>
      </div>

      {/* KPI banner */}
      <div className="mb-4 rounded-2xl bg-brand-soft/40 border border-brand/20 p-4 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-brand" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Totalizadores da seleção</div>
            <div className="text-sm text-navy">
              Meta <span className="font-semibold">{brl(totalMeta)}</span> · Faturado dos vendedores <span className="font-semibold">{brl(totalFat)}</span> · <span className={`font-semibold ${achTone(totalAch)}`}>{totalAch}% atingimento</span>
            </div>
            {data?.atualizado_em_sankhya ? (
              <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="cotas-sankhya-footer">
                Faturamento: Sankhya · atualizado às {new Date(data.atualizado_em_sankhya).toLocaleTimeString("pt-BR", {hour:"2-digit", minute:"2-digit"})}
              </div>
            ) : null}
          </div>
        </div>
        {dirtyIds.length ? (
          <div className="ml-auto text-xs text-warn font-semibold">
            {dirtyIds.length === 1 ? "1 linha" : `${dirtyIds.length} linhas`} com alterações não salvas
          </div>
        ) : null}
      </div>

      {/* Tabela — desktop (≥ 900 px) */}
      {!listaFiltrada.length ? (
        <EmptyState icon={Layers} title="Nenhum vendedor no filtro" subtitle="Ajuste a busca ou o filtro de papel." />
      ) : (
        <>
        <div className="rounded-2xl border border-black/5 bg-white overflow-hidden hidden sm:block">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground bg-slate-50">
                <th className="text-left px-4 py-3" style={{ maxWidth: 260 }}>Vendedor</th>
                {!hideGestorCol && <th className="text-left px-4 py-3 w-[160px]">Gestor</th>}
                <th className="text-right px-3 py-3 w-[170px]">Meta</th>
                <th className="text-right px-3 py-3 w-[140px]">Faturado (Sankhya)</th>
                <th className="text-right px-3 py-3 w-[60px]">%</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {listaFiltrada.map((r) => {
                const row = rows[r.user_id] || { goalCents: 0, invCents: 0 };
                const g = (row.goalCents || 0) / 100;
                const inv = (row.invCents || 0) / 100;
                const ach = g > 0 ? Math.round((inv / g) * 100) : 0;
                const isDirty = orig[r.user_id]
                  && row.goalCents !== orig[r.user_id].goalCents;
                return (
                  <tr key={r.user_id} className={`border-t border-black/5 ${isDirty ? "bg-warn-soft/40" : ""}`} data-testid={`lote-row-${r.user_id}`}>
                    <td className="px-4 py-2.5" style={{ maxWidth: 260 }}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={r.name} src={r.avatar} size={32} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-navy truncate">{r.name}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
                        </div>
                      </div>
                    </td>
                    {!hideGestorCol && <td className="px-4 py-2.5 text-sm text-muted-foreground truncate">{r.gestor || "—"}</td>}
                    <td className="px-3 py-2.5">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formatBRL(row.goalCents)}
                        onChange={(e) => setField(r.user_id, "goalCents", toCents(e.target.value))}
                        className="w-full min-w-[150px] h-9 px-2.5 rounded-md border border-slate-200 bg-white text-sm text-right focus:border-brand/60 focus:ring-2 focus:ring-brand/30 outline-none tabular-nums"
                        data-testid={`lote-goal-${r.user_id}`}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {r.sem_vinculo_sankhya ? (
                        <span className="text-xs text-muted-foreground italic" data-testid={`lote-sem-vinc-${r.user_id}`}>Sem vínculo</span>
                      ) : (
                        <span className="font-medium text-navy tabular-nums" data-testid={`lote-inv-${r.user_id}`}>{brl(inv)}</span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-semibold tabular-nums ${achTone(ach)}`}>{num(ach)}%</td>
                    <td className="px-2 py-2.5">
                      {isDirty ? (
                        <button
                          onClick={() => resetRow(r.user_id)}
                          title="Desfazer alterações desta linha"
                          data-testid={`lote-desfazer-${r.user_id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                        >
                          <Undo2 className="w-4 h-4" />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Cartões — mobile (< 640 px) */}
        <div className="sm:hidden space-y-3" data-testid="lote-mobile-list">
          {listaFiltrada.map((r) => {
            const row = rows[r.user_id] || { goalCents: 0, invCents: 0 };
            const g = (row.goalCents || 0) / 100;
            const inv = (row.invCents || 0) / 100;
            const ach = g > 0 ? Math.round((inv / g) * 100) : 0;
            const isDirty = orig[r.user_id] && row.goalCents !== orig[r.user_id].goalCents;
            return (
              <div key={r.user_id} className={`bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)] ${isDirty ? "ring-2 ring-warn/40" : ""}`} data-testid={`lote-card-${r.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={r.name} src={r.avatar} size={36} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-navy truncate">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.email}</div>
                  </div>
                  {isDirty ? (
                    <button onClick={() => resetRow(r.user_id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400" title="Desfazer alterações desta linha">
                      <Undo2 className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Meta</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatBRL(row.goalCents)}
                    onChange={(e) => setField(r.user_id, "goalCents", toCents(e.target.value))}
                    className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-right tabular-nums outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/30"
                    data-testid={`lote-goal-mob-${r.user_id}`}
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Faturado</div>
                    <div className="font-medium text-navy tabular-nums mt-0.5">
                      {r.sem_vinculo_sankhya ? <span className="text-xs text-muted-foreground italic">Sem vínculo</span> : brl(inv)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">%</div>
                    <div className={`font-semibold tabular-nums mt-0.5 ${achTone(ach)}`}>{num(ach)}%</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {dirtyIds.length ? (
        <div className="md:hidden mt-4 sticky bottom-4">
          <button
            onClick={save}
            disabled={saving}
            className="w-full h-11 rounded-xl bg-brand text-white font-semibold shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="salvar-lote-mobile-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar {dirtyIds.length === 1 ? "1 alteração" : `${dirtyIds.length} alterações`}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ======================================================================
// Aba 2: Minha cota real (12 campos, total automático, sem poluição)
// ======================================================================
function AbaMinhaCotaReal({ onDirtyChange }) {
  const { user } = useAuth();
  const role = user?.role;
  // PAINEL-1.8 (item 2): diretor geral enxerga/edita como admin — lista
  // todos os diretores e gerentes ativos para lançar a cota real de cada um.
  const isAdmin = role === "admin" || role === "diretor_geral";
  const isManager = role === "manager";
  const isDirector = role === "director";

  const [gestores, setGestores] = useState([]);
  const [gestorId, setGestorId] = useState("");
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mesesCents, setMesesCents] = useState(() => Array(12).fill(0));
  const [origCents, setOrigCents] = useState(() => Array(12).fill(0));
  const [anualInputCents, setAnualInputCents] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pessoas da seleção
  useEffect(() => {
    if (isAdmin) {
      (async () => {
        try {
          const r = await api.get("/ti/users");
          const list = (r.data || []).filter((u) => ["manager", "director"].includes(u.role) && u.is_active !== false);
          list.sort((a, b) => (a.role || "").localeCompare(b.role || "") || (a.name || "").localeCompare(b.name || ""));
          setGestores(list.map((u) => ({ id: u.id, nome: u.name, role: u.role })));
          if (list.length) setGestorId(list[0].id);
        } catch (e) { toast.error(errMsg(e)); }
      })();
      return;
    }
    if (isManager || isDirector) {
      setGestores([{ id: user.id, nome: user.name, role }]);
      setGestorId(user.id);
    }
  }, [isAdmin, isManager, isDirector, role, user?.id, user?.name]);

  const loadCota = useCallback(async () => {
    if (!gestorId) return;
    setLoading(true);
    try {
      const doc = await api.get(`/cotas-oficiais/${gestorId}?ano=${ano}`);
      const arr = (doc.data?.meses || Array(12).fill(0)).map((v) => Math.round((v || 0) * 100));
      // Importante: NÃO exibir o anual antigo. Mostramos apenas o que está nos
      // 12 campos (soma dos meses). Se os meses vierem zerados, total = 0.
      setMesesCents(arr);
      setOrigCents([...arr]);
      setAnualInputCents(0); // campo "Valor anual" (≥2027) sempre começa zerado
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  }, [gestorId, ano]);
  useEffect(() => { loadCota(); }, [loadCota]);

  const canEdit = isAdmin || ((isManager || isDirector) && gestorId === user?.id);

  const dirty = useMemo(
    () => mesesCents.some((c, i) => (c || 0) !== (origCents[i] || 0)),
    [mesesCents, origCents],
  );
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  const totalCents = useMemo(() => mesesCents.reduce((s, c) => s + (c || 0), 0), [mesesCents]);
  const totalReais = totalCents / 100;

  const setMes = (idx, cents) => {
    setMesesCents((prev) => {
      const copia = prev.slice();
      copia[idx] = cents;
      return copia;
    });
  };

  const salvar = async () => {
    if (!canEdit) return;
    if (!dirty) { toast.info("Nenhuma alteração para salvar"); return; }
    setSaving(true);
    try {
      await api.put(`/cotas-oficiais/${gestorId}`, {
        ano: Number(ano),
        anual: totalReais,                              // anual = soma dos 12 meses
        meses: mesesCents.map((c) => (c || 0) / 100),
      });
      toast.success("Cota real salva");
      await loadCota();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const distribuir = async () => {
    if (!canEdit) return;
    if (Number(ano) < 2027) { toast.error("Distribuição automática só a partir de 2027"); return; }
    const anualReais = anualInputCents / 100;
    if (!(anualReais > 0)) { toast.error("Informe o valor anual antes de distribuir"); return; }
    setSaving(true);
    try {
      // Chamada ao backend que já calcula a distribuição por dias úteis.
      // Nada é gravado em disco até o usuário clicar em "Salvar cota real".
      const r = await api.post(`/cotas-oficiais/${gestorId}/distribuir-preview`, {
        ano: Number(ano), anual: anualReais,
      }).catch(async () => {
        // Fallback: usa o endpoint antigo que grava, depois recarrega para
        // expor os valores calculados ANTES de permitir o salvamento final.
        return await api.post(`/cotas-oficiais/${gestorId}/distribuir`, { ano: Number(ano), anual: anualReais });
      });
      const mesesCalc = r.data?.meses || r.data?.linhas?.map((l) => l.cota_oficial) || [];
      if (mesesCalc.length === 12) {
        setMesesCents(mesesCalc.map((v) => Math.round((v || 0) * 100)));
        toast.success("Valores distribuídos — revise e clique em Salvar cota real");
      } else {
        await loadCota();
      }
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const anoNum = Number(ano);
  const showAnualAuto = anoNum >= 2027;

  const gestorAtual = gestores.find((g) => g.id === gestorId);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="text-base font-semibold text-navy" data-testid="aba-minha-titulo">
            Minha cota real · {ano}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Preencha os 12 meses — o total do ano é a soma dos meses.
          </p>
        </div>
        <button
          type="button"
          onClick={salvar}
          disabled={saving || !canEdit || !dirty}
          data-testid="salvar-cota-real-btn"
          className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar cota real
        </button>
      </div>

      <div className={`grid grid-cols-1 ${showAnualAuto ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3 mb-5`}>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Gestor</label>
          <select
            value={gestorId}
            onChange={(e) => setGestorId(e.target.value)}
            disabled={!isAdmin}
            className={INPUT + " mt-1"}
            data-testid="cota-real-gestor"
          >
            {gestores.map((g) => <option key={g.id} value={g.id}>{g.nome} · {g.role === "director" ? "Diretor" : "Gerente"}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Ano</label>
          <input
            type="number"
            value={ano}
            onChange={(e) => setAno(Number(e.target.value))}
            className={INPUT + " mt-1"}
            data-testid="cota-real-ano"
          />
        </div>
        {showAnualAuto ? (
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Valor anual</label>
            <div className="flex gap-2 mt-1">
              <input
                type="text"
                inputMode="numeric"
                value={formatBRL(anualInputCents)}
                onChange={(e) => setAnualInputCents(toCents(e.target.value))}
                disabled={!canEdit}
                className={INPUT + " tabular-nums"}
                data-testid="cota-real-anual-input"
              />
              <button
                type="button"
                onClick={distribuir}
                disabled={saving || !canEdit || !(anualInputCents > 0)}
                className="h-10 px-3 rounded-md bg-white border border-slate-200 text-sm text-navy font-semibold hover:bg-slate-50 disabled:opacity-60 whitespace-nowrap"
                data-testid="cota-real-distribuir"
              >
                Distribuir pelos dias úteis
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Preenche os 12 campos — nada é gravado até você clicar em “Salvar cota real”.</p>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="py-6 text-center text-muted-foreground text-sm flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cota-real-grade">
            {MESES.map((label, idx) => (
              <div key={idx}>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatBRL(mesesCents[idx] || 0)}
                  onChange={(e) => setMes(idx, toCents(e.target.value))}
                  disabled={!canEdit}
                  className={INPUT + " mt-1 tabular-nums text-right"}
                  data-testid={`cota-real-mes-${idx + 1}`}
                />
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 rounded-xl bg-brand-soft/40 border border-brand/20 px-4 py-3">
            <div className="text-sm">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Total do ano</div>
              <div className="text-navy text-xs mt-0.5">{gestorAtual?.nome || "—"} · {ano}</div>
            </div>
            <div className="text-xl font-bold text-navy tabular-nums" data-testid="cota-real-total">{brl(totalReais)}</div>
          </div>

          {/* Rodapé fixo em mobile */}
          <div className="md:hidden mt-4 sticky bottom-4">
            <button
              type="button"
              onClick={salvar}
              disabled={saving || !canEdit || !dirty}
              className="w-full h-11 rounded-xl bg-brand text-white font-semibold shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="salvar-cota-real-mobile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar cota real
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// ======================================================================
// PAINEL-1.7 — Aba "Cota da MPires" (diretor geral e admin)
// ======================================================================
const brlInput = (n) => (Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// 12 valores por mês da cota da MPires (ano). Logo abaixo, lista editável
// da cota oficial de CADA DIRETOR por mês, reaproveitando o mesmo editor
// (PUT /cotas-oficiais/{gestor_id}). Em "Distribuído aos diretores", soma
// mensal já aparece ao lado para conferência da diferença.
function AbaCotaMPires({ onDirtyChange }) {
  const now = new Date();
  const [ano, setAno] = useState(now.getFullYear());
  const [meses, setMeses] = useState(() => Array(12).fill(""));
  const [original, setOriginal] = useState(() => Array(12).fill(0));
  const [distribuido, setDistribuido] = useState(() => Array(12).fill(0));
  const [diretores, setDiretores] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingDir, setSavingDir] = useState({});

  const dirty = useMemo(() => meses.some((v, i) => Number((v || "0").toString().replace(",", ".")) !== Number(original[i] || 0)), [meses, original]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const load = useCallback(async () => {
    // PAINEL-1.8 (item 1): cada chamada é independente — um 404 numa não pode
    // zerar os dados da outra. Antes, o Promise.all com .catch único deixava
    // "Distribuído aos diretores" em R$ 0,00 quando a listagem falhava.
    let r1 = null; let r2 = [];
    const [res1, res2] = await Promise.allSettled([
      api.get(`/cotas-mpires?ano=${ano}`),
      api.get(`/cotas-oficiais/diretores?ano=${ano}`),
    ]);
    if (res1.status === "fulfilled") r1 = res1.value?.data || null;
    else toast.error(res1.reason?.response?.data?.detail || "Falha ao carregar Cota da MPires");
    if (res2.status === "fulfilled") r2 = res2.value?.data?.items || [];
    else toast.error(res2.reason?.response?.data?.detail || "Falha ao carregar cotas dos diretores");
    const m = (r1?.meses || Array(12).fill(0)).map((v) => Number(v || 0));
    setMeses(m.map((v) => v > 0 ? brlInput(v) : ""));
    setOriginal(m);
    setDistribuido(r1?.distribuido_diretores || Array(12).fill(0));
    setDiretores(r2);
  }, [ano]);
  useEffect(() => { load(); }, [load]);

  const salvar = async () => {
    const anual = meses.reduce((s, v) => s + Number((v || "0").toString().replace(/\./g, "").replace(",", ".")) || 0, 0);
    const vetor = meses.map((v) => Number((v || "0").toString().replace(/\./g, "").replace(",", ".")) || 0);
    setSaving(true);
    try {
      await api.put(`/cotas-mpires`, { ano: Number(ano), anual, meses: vetor });
      toast.success("Cota da MPires salva");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao salvar"); }
    finally { setSaving(false); }
  };

  const salvarDiretor = async (d, novoMeses) => {
    setSavingDir((s) => ({ ...s, [d.gestor_id]: true }));
    try {
      const anual = novoMeses.reduce((s, v) => s + Number(v || 0), 0);
      await api.put(`/cotas-oficiais/${d.gestor_id}`, { ano: Number(ano), anual, meses: novoMeses });
      toast.success(`Cota de ${d.gestor_nome} salva`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao salvar"); }
    finally { setSavingDir((s) => ({ ...s, [d.gestor_id]: false })); }
  };

  const anual = meses.reduce((s, v) => s + Number((v || "0").toString().replace(/\./g, "").replace(",", ".")) || 0, 0);
  const totalDistribuido = distribuido.reduce((s, v) => s + Number(v || 0), 0);
  const diff = anual - totalDistribuido;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm text-slate-600">Ano:</label>
        <select value={ano} onChange={(e) => setAno(Number(e.target.value))} data-testid="mpires-ano" className="h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <section className="bg-card rounded-2xl border border-black/5 p-4">
        <h2 className="text-base font-semibold text-navy">Cota da MPires · {ano}</h2>
        <p className="text-xs text-muted-foreground mb-3">Valor da empresa por mês (sem rateio). Base do cartão principal do diretor geral.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {MESES.map((lab, i) => (
            <label key={i} className="block">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{lab}</span>
              <input
                type="text" inputMode="decimal"
                value={meses[i]}
                onChange={(e) => setMeses((prev) => { const n = [...prev]; n[i] = e.target.value; return n; })}
                placeholder="0,00"
                data-testid={`mpires-mes-${i + 1}`}
                className="mt-1 w-full h-10 px-2.5 rounded-lg border border-input bg-white text-sm tabular-nums outline-none focus:ring-2 focus:ring-brand/40"
              />
              <span className="text-[10px] text-slate-400">Distribuído: {brlShort(distribuido[i])}</span>
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
          <div><span className="text-muted-foreground">Anual:</span> <b className="tabular-nums">{brl(anual)}</b></div>
          <div><span className="text-muted-foreground">Distribuído aos diretores:</span> <b className="tabular-nums">{brl(totalDistribuido)}</b></div>
          <div className={diff >= 0 ? "text-ok" : "text-bad"}>
            Diferença: <b className="tabular-nums">{brl(diff)}</b>
          </div>
        </div>
        <div className="mt-3">
          <button type="button" onClick={salvar} disabled={saving || !dirty} data-testid="mpires-salvar" className="h-10 px-4 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60">
            {saving ? "Salvando…" : "Salvar cota da MPires"}
          </button>
        </div>
      </section>

      <section className="bg-card rounded-2xl border border-black/5 p-4">
        <h2 className="text-base font-semibold text-navy">Cota oficial dos diretores · {ano}</h2>
        <p className="text-xs text-muted-foreground mb-3">Edite por mês. Cada diretor é salvo individualmente.</p>
        <div className="space-y-4">
          {diretores.map((d) => (
            <EditorCotaDiretor key={d.gestor_id} diretor={d} saving={!!savingDir[d.gestor_id]} onSave={salvarDiretor} />
          ))}
          {diretores.length === 0 && (
            <div className="text-sm text-muted-foreground">Nenhum diretor ativo.</div>
          )}
        </div>
      </section>
    </div>
  );
}

function EditorCotaDiretor({ diretor, saving, onSave }) {
  const [vals, setVals] = useState(() => (diretor.meses || Array(12).fill(0)).map((v) => Number(v) || 0));
  useEffect(() => { setVals((diretor.meses || Array(12).fill(0)).map((v) => Number(v) || 0)); }, [diretor]);
  const anual = vals.reduce((s, v) => s + Number(v || 0), 0);
  const dirty = vals.some((v, i) => Number(v || 0) !== Number((diretor.meses || [])[i] || 0));
  return (
    <div className="rounded-xl border border-black/5 p-3" data-testid={`mpires-diretor-${diretor.gestor_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold text-navy">{diretor.gestor_nome}</div>
        <div className="text-sm"><span className="text-muted-foreground">Anual:</span> <b className="tabular-nums">{brl(anual)}</b></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-2">
        {MESES.map((lab, i) => (
          <label key={i} className="block">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{lab}</span>
            <input
              type="text" inputMode="decimal"
              value={vals[i] ? brlInput(vals[i]) : ""}
              onChange={(e) => {
                const v = Number((e.target.value || "0").toString().replace(/\./g, "").replace(",", ".")) || 0;
                setVals((prev) => { const n = [...prev]; n[i] = v; return n; });
              }}
              placeholder="0,00"
              data-testid={`mpires-dir-${diretor.gestor_id}-mes-${i + 1}`}
              className="mt-1 w-full h-9 px-2 rounded-lg border border-input bg-white text-sm tabular-nums outline-none focus:ring-2 focus:ring-brand/40"
            />
          </label>
        ))}
      </div>
      <div className="mt-2">
        <button type="button" onClick={() => onSave(diretor, vals)} disabled={saving || !dirty} data-testid={`mpires-salvar-${diretor.gestor_id}`} className="h-9 px-3 rounded-lg bg-brand text-white text-xs font-semibold hover:bg-brand-hover disabled:opacity-60">
          {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}
